import APIService from "@/services/api.service";
import i18n from '@/plugins/i18n';

// PayPal haengt bei jedem Buttons().render() einen WEITEREN Button-Block an den
// Ziel-Container und leert ihn nicht (mehrfaches Oeffnen des Cart-Drawers,
// Planwechsel in finalCheckout, Planwechsel in adminSubscription). Deshalb genau
// EIN Button pro Container: dieser Store merkt sich je Selektor die Signatur der
// gerenderten Konfiguration und die Button-Instanz. Gleiche Signatur und der
// Knoten haengt noch im DOM -> kein zweiter Render; sonst close() + Container
// leeren + neu rendern. Modul-Scope, damit alle Aufrufer (auch
// adminSubscription.vue) dieselbe Instanz sehen.
const paypalMounts = {};

// Skeleton-Loader der Ansichten: eine Ansicht meldet sich mit ihrem
// Container-Selektor an (onPaypalMount) und erfaehrt dann, wann der
// Render-Versuch beendet ist - entweder steht der Button oder er kann nicht
// entstehen. Cart und subcheckout blenden damit ihren Skeleton-Loader aus.
const paypalReadyListeners = {};

// Der PayPal-Button sitzt in einem iframe und folgt dem Theme nicht. Bei einem
// Theme-Wechsel rendert der Service die angemeldeten Container darum neu.
// Schluessel = Container-Selektor, Wert = Render-Funktion der Ansicht.
const paypalThemeRerenders = {};

// Ein Watcher je Container meldet den Theme-Wechsel. Die Ansicht loest ihn in
// offPaypalMount (beforeDestroy), sonst ruft er spaeter ins leere DOM.
const paypalThemeWatches = {};

// Offene PayPal-Subscription: die Freigabe passiert bei PayPal, unser POST
// laeuft erst im SDK-Callback onApprove. Bricht der Callback ab (Rueckkehr aus
// dem PayPal-Fenster, Reload), besteht die Subscription bei PayPal, fehlt aber
// in unserer Datenbank. Deshalb die ID merken, sobald PayPal sie liefert, und
// beim naechsten Laden nachposten. Ein Schluessel pro Instanz.
const PENDING_SUBSCRIPTION_PREFIX = "liox.subscription.pending.";
const PENDING_SUBSCRIPTION_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

class PaymentService {
  createProviderURL(url, domClass) {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `${url}`;
    if (domClass) {
      script.className = `CCBillWidget${domClass}_29362`;
    } else {
      script.className = "";
    }
    return script;
  }

  // ---- Offene PayPal-Subscription: merken, nachposten, aufraeumen ----------

  pendingSubscriptionKey(host) {
    return `${PENDING_SUBSCRIPTION_PREFIX}${host || "getApiHost"}`;
  }

  // Merkt die bei PayPal angelegte Subscription. Ohne diesen Eintrag ist sie
  // verloren, wenn der onApprove-Callback nicht laeuft.
  storePendingSubscription(entry) {
    if (!entry || !entry.subscription_id) return;
    try {
      const record = {
        host: entry.host || "getApiHost",
        subscription_id: String(entry.subscription_id),
        plan: entry.plan,
        provider: entry.provider,
        ts: Date.now(),
      };
      window.localStorage.setItem(
        this.pendingSubscriptionKey(record.host),
        JSON.stringify(record)
      );
    } catch (error) {
      console.error("Pending subscription: speichern fehlgeschlagen", error);
    }
  }

  // Liest den offenen Eintrag. Ein Eintrag alter als sieben Tage wird verworfen
  // (der Plan kann sich in der Zwischenzeit geaendert haben).
  readPendingSubscription(host) {
    try {
      const raw = window.localStorage.getItem(this.pendingSubscriptionKey(host));
      if (!raw) return null;
      const record = JSON.parse(raw);
      if (!record || !record.subscription_id) return null;
      if (record.ts && Date.now() - record.ts > PENDING_SUBSCRIPTION_MAX_AGE) {
        this.clearPendingSubscription(host);
        return null;
      }
      return record;
    } catch (error) {
      console.error("Pending subscription: lesen fehlgeschlagen", error);
      return null;
    }
  }

  clearPendingSubscription(host) {
    try {
      window.localStorage.removeItem(this.pendingSubscriptionKey(host));
    } catch (error) {
      console.error("Pending subscription: loeschen fehlgeschlagen", error);
    }
  }

  // Postet eine offene Subscription nach. Den Stand entscheidet die API:
  // 201 = neu angelegt, 409 = steht schon in der Datenbank -> Eintrag weg.
  // Netz- und Serverfehler lassen den Eintrag stehen; der naechste Aufbau der
  // App versucht es erneut. Ohne Anmeldung passiert nichts.
  retryPendingSubscription(comp) {
    const host = "getApiHost";
    const record = this.readPendingSubscription(host);
    if (!record) return Promise.resolve(false);
    if (!comp || !comp.$store || !comp.$store.state.auth) {
      return Promise.resolve(false);
    }
    if (!comp.$store.state.auth.status.loggedIn) {
      return Promise.resolve(false);
    }
    return comp.$store
      .dispatch("post", {
        endpoint: "/api/v1/subscriptions",
        event: {
          plan: record.plan,
          provider: record.provider,
          subscription_id: record.subscription_id,
        },
        message: false,
        host: record.host,
      })
      .then(() => {
        this.clearPendingSubscription(record.host);
        // Der Zugang haengt am Benutzer-Objekt: neu laden, damit ein
        // nachgeholtes Abo ohne Seiten-Reload freischaltet.
        if (comp.$store) {
          comp.$store.dispatch("fetch", {
            endpoint: "/api/v1/users/me",
            state: "user",
          });
        }
        return true;
      })
      .catch((error) => {
        const status = error && error.response ? error.response.status : null;
        if (status === 409) {
          this.clearPendingSubscription(record.host);
        } else {
          console.log("Pending subscription: Nachposten fehlgeschlagen", error);
        }
        return false;
      });
  }

  isExplicit(config) {
    if (!config) return false;
    if (config.result && typeof config.result.explicit !== "undefined") {
      return !!config.result.explicit;
    }
    return !!config.explicit;
  }

  filterConsumerProviders(providers, config) {
    const explicit = this.isExplicit(config);
    return (providers || []).filter(
      (p) => (explicit ? p.type === "CCBill" : p.type === "Paypal")
    );
  }

  // Das Backend liefert die Umgebung in der Config: ConfigService setzt
  // environment = "sandbox" bei APP_ENV=dev, sonst "production".
  environment(config) {
    const raw =
      config && config.result
        ? config.result.environment
        : config && config.environment;
    if (typeof raw === "string" && raw.toLowerCase() === "sandbox") {
      return "sandbox";
    }
    return "production";
  }

  // In der Sandbox-Umgebung zahlt der Browser mit sandbox_id, in der
  // Live-Umgebung mit client_id. Browser und Server muessen dieselbe
  // Umgebung nutzen, deshalb KEIN Fallback auf das andere Set: fehlt der
  // Wert, ist der Provider nicht renderbar und wird sichtbar gemeldet.
  paymentClientId(provider, config) {
    const field =
      this.environment(config) === "sandbox" ? "sandbox_id" : "client_id";
    if (!provider) return { id: "", field, reason: "provider fehlt" };
    const value =
      typeof provider[field] === "string" ? provider[field].trim() : "";
    if (value === "") return { id: "", field, reason: `${field} fehlt` };
    if (value.startsWith("REPLACE_WITH")) {
      return { id: "", field, reason: `${field} ist Platzhalter` };
    }
    return { id: value, field, reason: "" };
  }

  configOf(comp) {
    return comp && comp.$store ? comp.$store.state.main.config : undefined;
  }

  // Ein Provider ist nur renderbar, wenn er existiert und das zur Umgebung
  // passende Feld (dev: sandbox_id, prod: client_id) einen echten Wert hat.
  // Leere Strings und Platzhalter aus der Seed-Migration (z. B.
  // REPLACE_WITH_PAYPAL_CLIENT_ID) zaehlen als nicht renderbar.
  isRenderableProvider(provider, config) {
    return this.paymentClientId(provider, config).id !== "";
  }

  // Kurze Diagnose fuer console.error: Typ des Providers und Grund, warum er
  // nicht gerendert werden kann. kind ist "Checkout" oder "Subscription".
  describeUnrenderableProvider(provider, kind, config) {
    const type = provider && provider.type ? provider.type : "unknown";
    if (!provider) return `${type}: provider fehlt`;
    const method = `render${type}${kind}`;
    if (typeof this[method] !== "function") {
      return `${type}: keine ${method}()`;
    }
    const { reason } = this.paymentClientId(provider, config);
    if (reason) return `${type}: ${reason}`;
    return `${type}: unbekannter Grund`;
  }

  // Provider-Liste abarbeiten und zaehlen, wie viele Buttons wirklich
  // gerendert wurden. Provider ohne Render-Methode oder ohne brauchbare
  // client_id werden uebersprungen. Wurde nichts gerendert, bekommt der
  // Nutzer eine sichtbare Meldung statt eines stummen leeren Bereichs.
  checkout(comp, providers, currency, callback) {
    const providerList = providers || [];
    let renderedCount = 0;

    providerList.forEach((provider) => {
      const payment = `render${provider && provider.type}Checkout`;
      const hasRenderer = typeof this[payment] === "function";
      if (
        !hasRenderer ||
        !this.isRenderableProvider(provider, this.configOf(comp))
      ) {
        console.log(`Payment method ${payment} is not implemented or not configured`);
        return;
      }
      this[payment](comp, provider, currency, callback);
      renderedCount++;
    });

    if (renderedCount === 0) {
      // Kein Button entstanden (Liste leer oder kein renderbarer Provider):
      // sichtbar melden statt eines leeren Bereichs. Der Skeleton-Loader
      // der Ansicht wird in jedem Fall beendet.
      if (providerList.length > 0) {
        console.error(
          "Payment checkout: kein Provider renderbar - " +
            providerList
              .map((p) =>
                this.describeUnrenderableProvider(p, "Checkout", this.configOf(comp))
              )
              .join("; ")
        );
      }
      APIService.setResponse({
        message: i18n.t('response.payment.provider_not_configured'),
        color: "red",
      });
      this.notifyPaypalMount("#paypal-checkout-container");
    }
  }

  renderEachPaymentProviderBtn(comp, providers, payload, callback, host = "getApiHost") {
    // Guard wie in checkout(): ein Provider-Typ ohne Render-Methode (z. B.
    // "SumUp" aus der Seed-Migration) darf die Schleife nicht abbrechen.
    // Ohne Guard wirft this[payment] einen TypeError und KEIN Provider wird
    // mehr gerendert (Symptom: leerer Button auf /subcheckout).
    // Zusaetzlich werden Provider ohne brauchbare client_id uebersprungen.
    const providerList = providers || [];
    let renderedCount = 0;

    providerList.forEach((provider) => {
      const payment = `render${provider && provider.type}Subscription`;
      const hasRenderer = typeof this[payment] === "function";
      if (
        !hasRenderer ||
        !this.isRenderableProvider(provider, this.configOf(comp))
      ) {
        console.log(`Payment method ${payment} is not implemented or not configured`);
        return;
      }
      this[payment](comp, provider, payload, callback, host);
      renderedCount++;
    });

    if (renderedCount === 0) {
      // Kein Button entstanden (Liste leer oder kein renderbarer Provider):
      // sichtbar melden statt eines leeren Bereichs. Der Skeleton-Loader
      // der Ansicht wird in jedem Fall beendet.
      if (providerList.length > 0) {
        console.error(
          "Payment subscription: kein Provider renderbar - " +
            providerList
              .map((p) =>
                this.describeUnrenderableProvider(
                  p,
                  "Subscription",
                  this.configOf(comp)
                )
              )
              .join("; ")
        );
      }
      APIService.setResponse({
        message: i18n.t('response.payment.provider_not_configured'),
        color: "red",
      });
      this.notifyPaypalMount("#paypalSubscriptionContainer");
    }
  }

  // SDK-Dedup (PayPal-Doku: eine SDK-Instanz pro Seite). Vor dem Anhängen eines
  // neuen <script> prüfen, ob das SDK mit derselben Konfiguration (URL inkl.
  // client-id + intent/currency) bereits im DOM hängt:
  // - gleiche URL vorhanden + window.paypal da → direkt rendern (kein 2. Script)
  // - gleiche URL vorhanden, noch nicht geladen → auf dessen load-Event warten
  // - window.paypal bereits da (andere SDK-URL, z. B. Subscription → Checkout)
  //   → kein neues Script anhängen; PayPal lädt das SDK ohnehin nur einmal.
  ensurePaypalSdk(src, onload, onerror) {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.paypal) {
        onload();
      } else {
        existing.addEventListener("load", onload);
        if (typeof onerror === "function") {
          existing.addEventListener("error", onerror);
        }
      }
      return;
    }
    if (window.paypal) {
      onload();
      return;
    }
    const script = this.createProviderURL(src);
    script.onload = onload;
    if (typeof onerror === "function") {
      script.onerror = onerror;
    }
    document.head.appendChild(script);
  }

  // Schliesst die zuletzt in diesem Container gerenderte Button-Instanz und
  // leert den Container. close() ist optional (nur wenn das SDK es anbietet):
  // die Garantie "genau ein Button" liefert das Leeren des Containers, close()
  // raeumt zusaetzlich die iframes der alten Instanz ab.
  resetPaypalContainer(selector) {
    const mount = paypalMounts[selector];
    if (mount) {
      if (mount.buttons && typeof mount.buttons.close === "function") {
        try {
          mount.buttons.close();
        } catch (error) {
          console.error("PayPal close:", error);
        }
      }
      delete paypalMounts[selector];
    }
    const container = document.querySelector(selector);
    if (container) {
      container.innerHTML = "";
    }
    return container;
  }

  // Eine Ansicht meldet sich fuer ihren Container an (onPaypalMount) und
  // erfaehrt damit, wann der Render-Versuch beendet ist. Ohne diese Meldung
  // bleibt ihr Skeleton-Loader stehen.
  onPaypalMount(selector, callback) {
    if (typeof callback !== "function") return;
    paypalReadyListeners[selector] = callback;
  }

  offPaypalMount(selector) {
    delete paypalReadyListeners[selector];
    delete paypalThemeRerenders[selector];
    if (typeof paypalThemeWatches[selector] === "function") {
      paypalThemeWatches[selector]();
      delete paypalThemeWatches[selector];
    }
  }

  // PayPal kennt keinen Theme-Schalter. Der Button traegt die Farbe deshalb aus
  // dem Theme: dunkles Theme schwarz, helles Theme gold (PayPal-Standard).
  // Ohne Vuetify-Theme bleibt es bei schwarz.
  paypalButtonColor(comp) {
    const dark =
      comp && comp.$vuetify && comp.$vuetify.theme
        ? !!comp.$vuetify.theme.dark
        : true;
    return dark ? "black" : "gold";
  }

  // Ein Theme-Wechsel aendert die Button-Farbe. Der gerenderte Button im iframe
  // folgt dem Theme aber nicht, darum rendert der Service die angemeldeten
  // Container neu. Der Watcher entsteht genau einmal je Container; die Signatur
  // enthaelt die Farbe, deshalb rendert mountPaypalButtons wirklich frisch.
  registerPaypalThemeRerender(comp, selector, rerender) {
    paypalThemeRerenders[selector] = rerender;
    if (
      !comp ||
      typeof comp.$watch !== "function" ||
      typeof paypalThemeWatches[selector] === "function"
    ) {
      return;
    }
    paypalThemeWatches[selector] = comp.$watch(
      () =>
        comp.$vuetify && comp.$vuetify.theme ? comp.$vuetify.theme.dark : null,
      () => {
        Object.keys(paypalThemeRerenders).forEach((key) => {
          try {
            paypalThemeRerenders[key]();
          } catch (error) {
            console.error("PayPal: Theme-Rerender fehlgeschlagen", error);
          }
        });
      }
    );
  }

  notifyPaypalMount(selector) {
    const callback = paypalReadyListeners[selector];
    if (typeof callback === "function") {
      callback();
    }
  }

  // Meldung, wenn das PayPal-SDK nicht laedt (Netzfehler, Blocker). Ohne sie
  // bleibt der Skeleton-Loader der Ansicht stehen.
  paypalSdkErrorHandler(selector) {
    return () => {
      console.error(`PayPal SDK: Laden fehlgeschlagen (${selector})`);
      APIService.setResponse({
        message: i18n.t("response.payment.payment_error"),
        color: "red",
      });
      this.notifyPaypalMount(selector);
    };
  }

  // Rendert GENAU EINEN PayPal-Button in den Container. Die Signatur beschreibt
  // alles, was der gerenderte Button festhaelt (Client-ID, Modus, Plan,
  // Waehrung). Nur bei identischer Signatur und noch vorhandenem Knoten bleibt
  // der bestehende Button stehen; jeder andere Fall rendert frisch.
  mountPaypalButtons(selector, signature, buildButtons, attempt = 0) {
    const container = document.querySelector(selector);
    // Die Ansicht baut ihr DOM erst nach dem providers-Fetch auf (Cart-Drawer),
    // der Render-Aufruf kommt aber aus demselben Promise. Deshalb auf den
    // Container warten - sonst bleibt der Button beim ersten Oeffnen leer.
    if (!container) {
      if (attempt < 30 && typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() =>
          this.mountPaypalButtons(selector, signature, buildButtons, attempt + 1)
        );
        return;
      }
      console.error(`PayPal: Container ${selector} fehlt`);
      this.notifyPaypalMount(selector);
      return;
    }
    const mount = paypalMounts[selector];
    const reusable =
      mount &&
      mount.signature === signature &&
      mount.el === container &&
      container.childNodes.length > 0;
    if (reusable) {
      this.notifyPaypalMount(selector);
      return;
    }
    this.resetPaypalContainer(selector);
    try {
      const buttons = buildButtons();
      paypalMounts[selector] = { signature, buttons, el: container };
      buttons.render(container);
    } catch (error) {
      // paypal.Buttons wirft, wenn das SDK fehlt oder blockiert wird.
      delete paypalMounts[selector];
      console.error("PayPal: Button-Render fehlgeschlagen", error);
      APIService.setResponse({
        message: i18n.t("response.payment.payment_error"),
        color: "red",
      });
    }
    this.notifyPaypalMount(selector);
  }

  // Der Server legt die Subscription an und liefert die Kennung (upsell-E.md
  // Option B). Preis, Gutschrift und ein spaeterer Start kommen von dort, nicht
  // aus dem Browser. Das SDK oeffnet mit der Kennung sein Freigabe-Fenster: der
  // Bedienablauf bleibt wie vorher (Klick auf den Button, Fenster, warten).
  requestSubscriptionApproval(comp, provider, payload, host) {
    const planId = payload && payload.plan ? payload.plan.id : null;
    const targetHost = host || "getApiHost";

    return comp.$store
      .dispatch("post", {
        endpoint: "/api/v1/subscriptions/approval-link",
        event: {
          plan: planId,
          provider: provider.id,
          return_url: window.location.href,
          cancel_url: window.location.href,
        },
        message: false,
        host: targetHost,
      })
      .then((answer) => {
        const result = answer && answer.result ? answer.result : answer;

        if (!result || !result.subscription_id) {
          throw new Error("Keine Subscription vom Server");
        }

        // Kein Merker an dieser Stelle: das Freigabe-Fenster ist noch offen, der
        // Kauf ist nicht abgeschlossen. Ein Nachposten wuerde eine Zeile fuer
        // einen offenen Kauf anlegen (Befund 2026-10-05). Der Merker entsteht
        // erst im onApprove.
        return result.subscription_id;
      })
      .catch((error) => {
        console.error("Subscription-Anlage fehlgeschlagen:", error);
        APIService.setResponse({
          message: i18n.t("response.payment.payment_error"),
          color: "red",
        });
        throw error;
      });
  }

  // Der Server legt die Subscription an und liefert den Freigabe-Link
  // (upsell-E.md Planwechsel, Option B, Stueck 10).
  renderPaypalSubscription(comp, provider, payload, callback, host) {
    const service = this;
    const selector = "#paypalSubscriptionContainer";
    const { id: clientId, reason } = this.paymentClientId(
      provider,
      this.configOf(comp)
    );
    if (!clientId) {
      console.error(`PayPal Subscription: ${reason}`);
      APIService.setResponse({
        message: i18n.t("response.payment.provider_not_configured"),
        color: "red",
      });
      this.notifyPaypalMount(selector);
      return;
    }
    const src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    const color = this.paypalButtonColor(comp);
    // Signatur = Client-ID + Plan-ID + Button-Farbe: nur bei unveraenderter
    // Konfiguration bleibt der bereits gerenderte Button stehen (sonst genau
    // ein neuer). Die Farbe kommt aus dem Theme, ein Theme-Wechsel rendert neu.
    const signature = `subscription|${clientId}|${payload.plan && payload.plan.paypal}|${color}`;
    this.registerPaypalThemeRerender(comp, selector, () =>
      this.renderPaypalSubscription(comp, provider, payload, callback, host)
    );
    const sdkError = this.paypalSdkErrorHandler(selector);
    this.ensurePaypalSdk(src, () => {
      this.mountPaypalButtons(
        selector,
        signature,
        () =>
          paypal.Buttons({
          style: {
            shape: "rect",
            color: color,
            layout: "vertical",
            label: "pay",
          },
          createSubscription: function (data, actions) {
            // Der Server legt die Subscription an (upsell-E.md Option B): Preis,
            // Gutschrift und ein spaeterer Start kommen von dort, nicht aus dem
            // Browser. Das SDK oeffnet mit der Kennung sein Freigabe-Fenster.
            return service.requestSubscriptionApproval(
              comp,
              provider,
              payload,
              host
            );
          },
          onApprove: function (data, actions) {
            payload.subscription_id = data.subscriptionID;
            payload.provider = provider.id;
            payload.plan = payload.plan.id;

            // Erst hier, mit der Freigabe des Kaufers, entsteht der Merker: der
            // Kauf ist abgeschlossen, unser POST kann noch ausfallen. Ein
            // Nachposten ohne Freigabe legt eine Zeile fuer einen offenen Kauf
            // an (Befund 2026-10-05).
            service.storePendingSubscription({
              host: host,
              subscription_id: payload.subscription_id,
              plan: payload.plan,
              provider: provider.id,
            });

            comp.$store
              .dispatch("post", {
                endpoint: "/api/v1/subscriptions",
                event: payload,
                message: false,
                host: host,
              })
              .then((subscription) => {
                // In unserer Datenbank angelegt: der Merker hat seine Aufgabe.
                service.clearPendingSubscription(host);
                // Die Ansicht uebernimmt die Erfolgsanzeige (Erfolgsseite und
                // frisches Benutzer-Objekt). Ohne sie bleibt das Gate bis zum
                // naechsten Seitenaufbau zu.
                if (comp && typeof comp.onSubscriptionSuccess === "function") {
                  comp.onSubscriptionSuccess(subscription);
                } else if (
                  callback.length > 0 &&
                  typeof APIService[callback[0]] == "function"
                ) {
                  APIService[callback[0]](callback[1]);
                } else {
                  APIService.setResponse({
                    message: i18n.t('response.payment.success_title'),
                    color: "green",
                  });
                }
              })
              .catch((error) => {
                // Ein 409 heisst: die Zeile steht schon. Der Webhook kann
                // schneller sein als dieser POST. Das ist kein Fehler.
                const status = error && error.response ? error.response.status : null;
                if (status === 409) {
                  service.clearPendingSubscription(host);
                  if (comp && comp.$store) {
                    comp.$store.dispatch("fetch", {
                      endpoint: "/api/v1/users/me",
                      state: "user",
                    });
                  }
                  if (comp && typeof comp.onSubscriptionSuccess === "function") {
                    comp.onSubscriptionSuccess(null);
                  } else {
                    APIService.setResponse({
                      message: i18n.t('response.payment.success_title'),
                      color: "green",
                    });
                  }
                  return;
                }
                console.log(error);
                APIService.setResponse({
                  message: i18n.t('response.payment.admin_cannot_pay'),
                  color: "red",
                });
              });
          },
          onCancel: function (data) {
            APIService.setResponse({
              message: i18n.t('response.payment.payment_cancelled'),
              color: "blue",
            });
          },
          onError: function (err) {
            console.error("PayPal Subscription-Fehler:", err);
            APIService.setResponse({
              message: i18n.t('response.payment.payment_error'),
              color: "red",
            });
          },
        })
      );
    }, sdkError);
  }

  renderPaypalCheckout(comp, provider, currency, callback) {
    const selector = "#paypal-checkout-container";
    const { id: clientId, reason } = this.paymentClientId(
      provider,
      this.configOf(comp)
    );
    if (!clientId) {
      console.error(`PayPal Checkout: ${reason}`);
      APIService.setResponse({
        message: i18n.t("response.payment.provider_not_configured"),
        color: "red",
      });
      this.notifyPaypalMount(selector);
      return;
    }
    const src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}`;
    const color = this.paypalButtonColor(comp);
    // Signatur = Client-ID + Waehrung + Button-Farbe: beim erneuten Oeffnen des
    // Cart-Drawers wird derselbe Button wiederverwendet statt ein zweiter
    // angehaengt. Die Farbe kommt aus dem Theme, ein Theme-Wechsel rendert neu.
    const signature = `checkout|${clientId}|${currency}|${color}`;
    this.registerPaypalThemeRerender(comp, selector, () =>
      this.renderPaypalCheckout(comp, provider, currency, callback)
    );
    const sdkError = this.paypalSdkErrorHandler(selector);
    this.ensurePaypalSdk(src, () => {
      this.mountPaypalButtons(
        selector,
        signature,
        () =>
          paypal.Buttons({
          style: {
            shape: "rect",
            color: color,
            layout: "vertical",
            label: "pay",
          },
          createOrder() {
            return comp.$store
              .dispatch("post", {
                endpoint: "/api/v1/orders",
                event: { provider: provider.id },
                message: false,
              })
              .then((order) => order.result.id);
          },
          onApprove(data) {
            return comp.$store
              .dispatch("post", {
                endpoint: "/api/v1/orders/" + data.orderID + "/capture",
                event: { provider: provider.id },
                message: false,
              })
              .then((orderData) => {
                comp.$store.dispatch("fetch", {
                  endpoint: `/api/v1/users/me`,
                });
                comp.$store.dispatch("delete", {
                  endpoint: `/api/v1/cart/`,
                  message: false,
                });
                if (
                  callback.length > 0 &&
                  typeof APIService[callback[0]] == "function"
                ) {
                  APIService[callback[0]](callback[1]);
                } else {
                  APIService.setResponse({
                    message: i18n.t('response.payment.thank_you_purchase'),
                    color: "green",
                  });
                }
              })
              .catch((error) => {
                console.log(error);
                APIService.setResponse({
                  message: i18n.t('response.payment.admin_cannot_pay'),
                  color: "red",
                });
              });
          },
          onCancel: function (data) {
            APIService.setResponse({
              message: i18n.t('response.payment.payment_cancelled'),
              color: "blue",
            });
          },
          onError: function (err) {
            console.error("PayPal Checkout-Fehler:", err);
            APIService.setResponse({
              message: i18n.t('response.payment.payment_error'),
              color: "red",
            });
          },
        })
      );
    }, sdkError);
  }

  // Signatur-Diskrepanz (bewusst NICHT angepasst): checkout() ruft diese Methode
  // mit (comp, provider, currency, callback), definiert ist (comp, callback).
  // Die Extra-Argumente werden von der Methode ignoriert -> verhaltensneutral.
  // Der Pfad ist faktisch tot: bei explicit=true ueberspringt
  // App.vue::openCartDrawer den providers-Fetch + checkout() komplett (der
  // statische Template-CCBill-Button uebernimmt); bei nicht-explicit filtert
  // filterConsumerProviders() auf 'Paypal'. Falls CCBill je ueber checkout()
  // laufen soll, Signatur hier auf (comp, provider, currency, callback) anpassen.
  renderCCBillCheckout(comp, callback) {
    var initialPrice = comp.$store.state.main.cart.sum * 1.19;
    var userId = comp.$store.state.main.user.result.id;
    var currencyCode = 978;
    var initialPeriod = 2;

    if (initialPrice != 0) {
      comp.$store
        .dispatch("post", {
          endpoint: "/api/v1/orders/createFormDigestSingle",
          event: {
            initialPrice,
            currencyCode,
            initialPeriod,
          },
          message: false,
        })
        .then((response) => {
          const ccbillUrl = `https://api.ccbill.com/wap-frontflex/flexforms/d34e4649-2422-41d9-9f3b-ac8406e34da2?clientSubacc=0001&initialPrice=${initialPrice}&initialPeriod=${initialPeriod}&currencyCode=${currencyCode}&formDigest=${response.hash}&userId=${userId}&orderType=order`;

          // URL nach außen geben – die Komponente übernimmt den Rest
          if (typeof callback === 'function') {
            callback(ccbillUrl);
          }
        })
        .catch((error) => {
          console.error("CCBill Checkout Digest fehlgeschlagen:", error);
          if (typeof callback === 'function') {
            callback(null);
          }
        });
    }
  }

  renderCCBillSubscription(comp, provider, payload, currency, callback) {
    // The bridge of a downgrade (upsell-E.md T6, decision A of Kevin): the first
    // period carries the CCBill minimum price and the remaining days, the
    // recurring price stays the price of the new plan. The bridge states net
    // values (decision of Kevin 2026-10-02), like the plan price.
    var bridge = payload.bridge || null;

    // 19 percent on both values: the first payment and every renewal match.
    // CCBill takes two decimals: 2.50 net becomes 2.98 and not 2.975.
    var round2 = function (value) {
      return Math.round(value * 100) / 100;
    };

    var initialPrice = round2(
      (bridge ? bridge.initial_price : payload.plan.price) * 1.19
    );
    var initialPeriod = bridge ? bridge.initial_period : 30;
    var recurringPrice = round2(payload.plan.price * 1.19);
    var recurringPeriod = 30;
    var numRebills = 99;
    var currencyCode = 978;
    var planId = payload.plan.id;
    var providerId = payload.provider;

    const storeUser = comp.$store.state.main.user.result;

    let userId = null;
    if (storeUser && storeUser.id) {
      userId = storeUser.id;
    } else if (payload.userId) {
      userId = payload.userId;
    }
    if (!userId) {
      console.error("UserId fehlt")
      return
    }

    comp.$store
      .dispatch("post", {
        endpoint: "/api/v1/orders/createFormDigestSubscription",
        event: {
          initialPrice,
          initialPeriod,
          recurringPrice,
          recurringPeriod,
          numRebills,
          currencyCode,
        },
        message: false,
      })
      .then((response) => {
        const ccbillUrl = `https://api.ccbill.com/wap-frontflex/flexforms/e7ceee25-f46e-487c-a411-b22a994ea9ef?clientSubacc=0001&initialPrice=${initialPrice}&initialPeriod=${initialPeriod}&recurringPrice=${recurringPrice}&recurringPeriod=${recurringPeriod}&numRebills=${numRebills}&currencyCode=${currencyCode}&formDigest=${response.hash}&planId=${planId}&userId=${userId}&providerId=${providerId}&orderType=subscription`;
        const container = document.getElementById(
          "ccbillSubscriptionWidgetContainer"
        );
        if (!container) return;
        container.innerHTML = "";
        const ccbillBtn = document.createElement("button");
        ccbillBtn.type = "button";
        ccbillBtn.className = "v-btn v-btn--block py-3 px-4 v-btn--contained theme--dark primary";

        const btnContent = document.createElement("span");
        btnContent.className = "v-btn__content";
        btnContent.textContent = i18n.t('payment.credit_card_button');
        ccbillBtn.onclick = () => {
          ccbillBtn.disabled = true;

          // Kompletten Button-Inhalt ersetzen
          ccbillBtn.innerHTML = `
            <span class="v-btn__content" style="display:flex;align-items:center;gap:8px;">
              <svg style="animation:ccbill-spin 0.8s linear infinite;width:20px;height:20px;flex-shrink:0;"
                  viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="white" stroke-width="3" stroke-linecap="round"/>
              </svg>
              ${i18n.t('shop.video_cart.loading')}
            </span>
          `;

          // Spin-Animation ins DOM injizieren (einmalig)
          if (!document.getElementById('ccbill-spin-style')) {
            const style = document.createElement('style');
            style.id = 'ccbill-spin-style';
            style.textContent = `
              @keyframes ccbill-spin {
                from { transform: rotate(0deg); }
                to   { transform: rotate(360deg); }
              }
            `;
            document.head.appendChild(style);
          }

          window.location.href = ccbillUrl;
        };

        ccbillBtn.appendChild(btnContent);
        container.appendChild(ccbillBtn);
      });
  }

  // Click-getriebener CCBill-Subscription-Flow (subcheckout).
  // Baut die CCBill-URL on click über createFormDigestSubscription und gibt sie
  // per callback zurück. Kein Provider-Datensatz (providers-Tabelle) nötig —
  // providerId wird nur gesetzt, wenn payload.provider vorhanden ist.
  // Der Backend-Webhook toleriert fehlendes X-providerId (OrderController:451).
  createCCBillSubscriptionUrl(comp, payload, callback) {
    // 19 percent on both values: the first payment and every renewal match.
    var initialPrice = payload.plan.price * 1.19;
    var initialPeriod = 30;
    var recurringPrice = payload.plan.price * 1.19;
    var recurringPeriod = 30;
    var numRebills = 99;
    var currencyCode = 978;
    var planId = payload.plan.id;

    const storeUser = comp.$store.state.main.user.result;

    let userId = null;
    if (storeUser && storeUser.id) {
      userId = storeUser.id;
    } else if (payload.userId) {
      userId = payload.userId;
    }
    if (!userId) {
      console.error("UserId fehlt");
      if (typeof callback === "function") callback(null);
      return;
    }

    const providerParam = payload.provider ? `&providerId=${payload.provider}` : "";

    comp.$store
      .dispatch("post", {
        endpoint: "/api/v1/orders/createFormDigestSubscription",
        event: {
          initialPrice,
          initialPeriod,
          recurringPrice,
          recurringPeriod,
          numRebills,
          currencyCode,
        },
        message: false,
      })
      .then((response) => {
        const ccbillUrl = `https://api.ccbill.com/wap-frontflex/flexforms/e7ceee25-f46e-487c-a411-b22a994ea9ef?clientSubacc=0001&initialPrice=${initialPrice}&initialPeriod=${initialPeriod}&recurringPrice=${recurringPrice}&recurringPeriod=${recurringPeriod}&numRebills=${numRebills}&currencyCode=${currencyCode}&formDigest=${response.hash}&planId=${planId}&userId=${userId}${providerParam}&orderType=subscription`;

        if (typeof callback === "function") {
          callback(ccbillUrl);
        }
      })
      .catch((error) => {
        console.error("CCBill Subscription Digest fehlgeschlagen:", error);
        if (typeof callback === "function") {
          callback(null);
        }
      });
  }

}

export default new PaymentService();
